/**
 * Public transport, as the game runs it: trips, each a vehicle on its way
 * along a line, wherever they come from. Västtrafik's live departures
 * (`vasttrafik.ts`) give real ones, their stops, times and the way they
 * drive; OpenStreetMap's bus routes, elsewhere, give the way and the stops,
 * and a plausible timetable is made up for them.
 *
 * Where a trip's vehicle should be at a moment is read off its timetable:
 * standing at a stop from its arrival to its departure, and between two
 * stops along its way in proportion to the time. A bus in the game paces
 * itself to that (`three/traffic.ts`), so it keeps to the real timetable
 * while it drives among the cars.
 *
 * Pure: no fetch, no DOM.
 */

/** How a line's vehicles look: Västtrafik's three designs of bus, and a plain one painted the operator's colour. */
export type BusLook = 'area' | 'express' | 'metro' | 'plain';

export interface TransitLine {
  /** What is on the front of the bus: "16", "Rosa express". */
  name: string;
  mode: 'bus' | 'tram' | 'ferry' | 'train' | 'other';
  look: BusLook;
  /** The line's own colours, #rrggbb: its sign's background, text and border. */
  background: string;
  foreground: string;
  border: string;
}

export interface TransitStop {
  name: string;
  lat: number;
  lon: number;
  /** Best known arrival and departure, milliseconds since the epoch. */
  arrive: number;
  depart: number;
}

export interface TransitTrip {
  /** Stable for the trip through the day: one vehicle in the game per id. */
  id: string;
  line: TransitLine;
  /** Where it is going, as on its sign. */
  headsign: string;
  stops: TransitStop[];
  /** The way it drives, lat, lon pairs, from its first stop to its last. */
  path: number[];
}

/** A trip laid out in a plane: its way as x, z points and how far along each is, and how far along each stop is. */
export interface TripLayout {
  xs: Float64Array;
  zs: Float64Array;
  along: Float64Array;
  stopAlong: Float64Array;
  length: number;
}

/**
 * Lay a trip out, `project` taking lat, lon to plane x, z metres. Each stop is
 * placed where its way passes nearest it, in order, never back along it.
 */
export function layoutTrip(trip: TransitTrip, project: (lat: number, lon: number) => [number, number]): TripLayout {
  const n = trip.path.length / 2;
  const xs = new Float64Array(n), zs = new Float64Array(n), along = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const [x, z] = project(trip.path[i * 2], trip.path[i * 2 + 1]);
    xs[i] = x;
    zs[i] = z;
    if (i > 0) along[i] = along[i - 1] + Math.hypot(x - xs[i - 1], z - zs[i - 1]);
  }
  const stopAlong = new Float64Array(trip.stops.length);
  let from = 0;
  trip.stops.forEach((stop, k) => {
    const [x, z] = project(stop.lat, stop.lon);
    let best = Infinity, at = along[from] ?? 0, bestSeg = from;
    for (let i = from; i + 1 < n; i++) {
      const ax = xs[i], az = zs[i], dx = xs[i + 1] - ax, dz = zs[i + 1] - az;
      const len2 = dx * dx + dz * dz;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2)) : 0;
      const d = Math.hypot(ax + dx * t - x, az + dz * t - z);
      // The nearest pass, but the first one that comes close: a loop's way
      // back past the same stop is a later stop.
      if (d < best - 1e-6) { best = d; at = along[i] + t * Math.sqrt(len2); bestSeg = i; }
      if (best < 15 && d > best + 60) break;
    }
    stopAlong[k] = Math.max(at, k > 0 ? stopAlong[k - 1] : 0);
    from = bestSeg;
  });
  return { xs, zs, along, stopAlong, length: n > 0 ? along[n - 1] : 0 };
}

/** Where a trip's vehicle should be at `now`: how far along its way, and at or between which stops. */
export interface Scheduled {
  along: number;
  /** The stop it is standing at, or -1 between stops. */
  at: number;
  /** The next stop it comes to, or the number of stops past the last. */
  next: number;
  /** Before its first departure, or past its last arrival. */
  before: boolean;
  after: boolean;
}

/**
 * Where the timetable has a trip's vehicle at `now` (ms): at a stop from its
 * arrival to its departure, and between two stops eased along in their time,
 * slow away from one and slow into the next, as a bus drives it.
 */
export function scheduledAt(trip: TransitTrip, layout: TripLayout, now: number): Scheduled {
  const stops = trip.stops, s = layout.stopAlong;
  const last = stops.length - 1;
  if (last < 0) return { along: 0, at: -1, next: 0, before: true, after: false };
  if (now < stops[0].depart) return { along: s[0], at: 0, next: 1, before: now < stops[0].arrive, after: false };
  for (let k = 0; k <= last; k++) {
    if (now < stops[k].arrive) {
      const from = stops[k - 1].depart, to = stops[k].arrive;
      const u = to > from ? (now - from) / (to - from) : 1;
      const e = u * u * (3 - 2 * u);
      return { along: s[k - 1] + (s[k] - s[k - 1]) * e, at: -1, next: k, before: false, after: false };
    }
    if (now <= stops[k].depart) return { along: s[k], at: k, next: k + 1, before: false, after: false };
  }
  return { along: s[last], at: -1, next: last + 1, before: false, after: true };
}

/** A point along a laid-out way, and its direction there. */
export function pointAlong(layout: TripLayout, d: number): { x: number; z: number; dx: number; dz: number } {
  const { xs, zs, along } = layout;
  const n = xs.length;
  if (n === 0) return { x: 0, z: 0, dx: 0, dz: -1 };
  if (n === 1) return { x: xs[0], z: zs[0], dx: 0, dz: -1 };
  let lo = 0, hi = n - 1;
  d = Math.max(0, Math.min(along[n - 1], d));
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (along[mid] <= d) lo = mid; else hi = mid;
  }
  const span = along[hi] - along[lo];
  const t = span > 0 ? (d - along[lo]) / span : 0;
  const dx = xs[hi] - xs[lo], dz = zs[hi] - zs[lo], len = Math.hypot(dx, dz) || 1;
  return { x: xs[lo] + dx * t, z: zs[lo] + dz * t, dx: dx / len, dz: dz / len };
}

/**
 * How far along a laid-out way the point (x, z) is, looking from `near`
 * (along) by up to `reach` metres either side: the vehicle's own progress,
 * found where it is now rather than anywhere the way passes by.
 */
export function alongNear(layout: TripLayout, x: number, z: number, near: number, reach = 80): { along: number; off: number } {
  const { xs, zs, along } = layout;
  let best = Infinity, at = near;
  for (let i = 0; i + 1 < xs.length; i++) {
    if (along[i + 1] < near - reach || along[i] > near + reach) continue;
    const ax = xs[i], az = zs[i], dx = xs[i + 1] - ax, dz = zs[i + 1] - az;
    const len2 = dx * dx + dz * dz;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2)) : 0;
    const d = Math.hypot(ax + dx * t - x, az + dz * t - z);
    if (d < best) { best = d; at = along[i] + t * Math.sqrt(len2); }
  }
  return { along: at, off: best };
}
